import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatSort, MatSortHeader } from '@angular/material/sort'
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
} from '@angular/material/table'
import {
  ElderDurationInputComponent,
  ElderFormFieldDenseDirective,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderSelectComponent,
  ElderSinglePaneWrapperComponent,
  ElderTableComponent,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { Duration } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { GroupedEdge } from '../grouped-edge'
import { GroupedEdgeTarget } from '../grouped-edge-target'
import { ShopfloorLayoutContext } from '../shopfloor-layout-context'
import { ShopfloorLayoutSpecNode } from '../shopfloor-layout-spec-node'
import { TransportCost } from '../transport-cost'

export interface IShopFloorEditorDialogOptions {
  readonly context: ShopfloorLayoutContext
  readonly groupedEdges: GroupedEdge[]
  readonly selectedEdge?: GroupedEdge
}

@Component({
  selector: 'app-shopfloor-layout-editor-detail-dialog',
  templateUrl: './shopfloor-layout-editor-detail-dialog.component.html',
  styleUrls: ['./shopfloor-layout-editor-detail-dialog.component.scss'],
  imports: [
    ElderSinglePaneWrapperComponent,
    ElderPaneHeaderComponent,
    ElderPaneTitleComponent,
    ElderPaneContentComponent,
    ElderPaneSubtitleComponent,
    FormsModule,
    MatFormField,
    MatLabel,
    ElderSelectComponent,
    ElderTableComponent,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    ElderFormFieldDenseDirective,
    ElderDurationInputComponent,
    MatIconButton,
    MatIcon,
    MatButton,
    AsyncPipe,
    ElderTranslatePipe,
  ],
})
export class ShopfloorLayoutEditorDetailDialogComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _context$: BehaviorSubject<ShopfloorLayoutContext> = new BehaviorSubject(null)
  private readonly _groupedEdge$: BehaviorSubject<GroupedEdge> = new BehaviorSubject(null)

  // Suggestions
  private readonly _possibleSources: ShopfloorLayoutSpecNode[]
  public sourceSuggestions: ShopfloorLayoutSpecNode[]
  public targetSuggestions: GroupedEdgeTarget[]

  // Selected nodes
  public selectedSource: ShopfloorLayoutSpecNode
  private readonly _selectedTargets$ = new BehaviorSubject<GroupedEdgeTarget[]>([])

  // Props
  private _creationMode: boolean

  @Input()
  public embedded = false
  dialogRef = inject<MatDialogRef<ShopfloorLayoutEditorDetailDialogComponent>>(MatDialogRef)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const data = inject<IShopFloorEditorDialogOptions>(MAT_DIALOG_DATA)

    this.context = data.context
    this.groupedEdge = data.selectedEdge

    // Suggestions for source dropdown
    if (!this.creationMode) {
      this.sourceSuggestions = [this.groupedEdge.source]
    } else {
      const usedSources = data.groupedEdges.map((ge) => ge.source)
      this._possibleSources = this.context.nodes.filter((n) => !usedSources.includes(n))
      this.sourceSuggestions = this._possibleSources
    }

    // Suggestions for target dropdown
    this.targetSuggestions = this.context.nodes
      .filter((n) => !this.groupedEdge.targets.map((t) => t.node.id).includes(n.id))
      .map((n) => new GroupedEdgeTarget(n.id, n, new TransportCost(Duration.ZERO.toString())))
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  // Context
  public get context$(): Observable<ShopfloorLayoutContext> {
    return this._context$.asObservable()
  }

  public get context(): ShopfloorLayoutContext {
    return this._context$.getValue()
  }

  // Grouped Edge
  public get groupedEdge$(): Observable<GroupedEdge> {
    return this._groupedEdge$.asObservable()
  }

  public get groupedEdge(): GroupedEdge {
    return this._groupedEdge$.getValue()
  }

  // Selected targets
  public get selectedTargets$(): Observable<GroupedEdgeTarget[]> {
    return this._selectedTargets$.asObservable()
  }

  public get selectedTargets(): GroupedEdgeTarget[] {
    return this._selectedTargets$.getValue()
  }

  public set selectedTargets(groupedEdgeTargets: GroupedEdgeTarget[]) {
    this._selectedTargets$.next(groupedEdgeTargets)
  }

  // Misc
  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  @Input()
  public set context(context: ShopfloorLayoutContext) {
    if (context) {
      this._context$.next(context)
    }
  }

  @Input()
  public set groupedEdge(edge: GroupedEdge | undefined) {
    if (edge) {
      this._creationMode = !edge.source
      this._groupedEdge$.next(edge)

      this.selectedSource = this.groupedEdge.source
      this.selectedTargets = this.groupedEdge.targets.map(
        (t) => new GroupedEdgeTarget(t.node.id, t.node, new TransportCost(t.transportCost.duration))
      ) // Deep copy
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  // Buttons
  public onApply(): void {
    const updatedGroupedEdge = new GroupedEdge(this.selectedSource, this.selectedTargets)
    this.dialogRef.close(updatedGroupedEdge)
  }

  public onClear(): void {
    this.selectedTargets = []
  }

  public onClose(): void {
    this.dialogRef.close()
  }

  // Source
  public onSourceSelected(id: string): void {
    const targetsToExclude = this.selectedTargets.map((st) => st.node.id)
    targetsToExclude.push(id)
    this.updateTargetSuggestions(targetsToExclude)
  }

  // Targets
  public onTargetSelected(newTarget: GroupedEdgeTarget): void {
    this.log.debug('Selected new target:', newTarget)
    this.selectedTargets = [...this.selectedTargets, newTarget]
    this.updateTargetSuggestions([
      ...this.selectedTargets.map((ut) => ut.node.id),
      this.selectedSource.id,
    ])
    this.updateSourceSuggestions(this.selectedTargets.map((ut) => ut.node))
  }

  public onDeleteTargetEntry(targetToDelete: GroupedEdgeTarget): void {
    this.log.debug('Deleted target:', targetToDelete)
    this.selectedTargets = this.selectedTargets.filter(
      (st) => st.node.id !== targetToDelete.node.id
    )
    this.updateTargetSuggestions(this.selectedTargets.map((ut) => ut.node.id))
    this.updateSourceSuggestions(this.selectedTargets.map((ut) => ut.node))
  }

  public onTargetEntryClick($event: Event): void {
    this.log.debug('Entry clicked:', $event)
  }

  // Transport duration
  public onDurationChange(targetId: string, $event: Duration): void {
    this.log.debug('Duration changed:', targetId, $event)
    this.selectedTargets.find((t) => t.node.id === targetId).transportCost.duration =
      $event.toString()
  }

  // Resolvers
  public get nodeNameResolver(): (node: ShopfloorLayoutSpecNode) => string {
    return (n) => n?.workCenter?.number + ' ' + n?.workCenter?.name
  }

  public get targetNameResolver(): (target: GroupedEdgeTarget) => string {
    return (gt) => this.nodeNameResolver(gt?.node)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private updateTargetSuggestions(excludedNodeIds: string[]): void {
    this.targetSuggestions = this.context.nodes
      .filter((n) => !excludedNodeIds.includes(n.id))
      .map((n) => new GroupedEdgeTarget(n.id, n, new TransportCost(Duration.ZERO.toString())))
  }

  private updateSourceSuggestions(excludedNodes: ShopfloorLayoutSpecNode[]): void {
    if (this.creationMode) {
      // Sources can only be changed in creation mode, only update if necessary
      this.sourceSuggestions = this._possibleSources.filter((n) => !excludedNodes.includes(n))
    }
  }
}
